import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import CloseButton from "react-bootstrap/CloseButton";
import "./style.scss";
import CommonButton from "../../commonButton";
import { getFieldConfig } from "../utils/fieldConfig";
import InfoButton from "../../../assets/images/custom_documentation/info_button.svg";
import DropdownDelete from "../../../assets/images/custom_documentation/dropdown-delete.svg";

const SettingsTaskModal = ({
  show,
  onHide,
  field = null,
  modalSettingsObj,
  setModalSettingsObj,
  menuArray,
  updateField,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const [settings, setSettings] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState([""]);
  const [multipleOptions, setMultipleOptions] = useState(false);

  useEffect(() => {
    if (field && menuArray) {
      // Find field config in menuArray
      const findConfig = (items) => {
        for (const item of items) {
          if (item.name === field?.type || item.label === field?.type) {
            return item;
          }
          if (item.child) {
            const childConfig = findConfig(item.child);
            if (childConfig) return childConfig;
          }
        }
        return null;
      };

      const config = findConfig(menuArray);
      if (config) {
        const fieldId = field?.id || null;
        
        setSettings((prev) => ({
          ...prev,
          [fieldId]: {
            ...prev[fieldId],
            required:
              field.needed !== undefined
                ? field.needed
                : config.required || false,
            characterLimit: config.characterLimit
              ? field.characterLimit || prev[fieldId]?.characterLimit || ""
              : "",
            fontSize: field.fontSize || prev[fieldId]?.fontSize || "",
            fontStyle: field.fontStyle || prev[fieldId]?.fontStyle || "",
            multiple_options:
              field.multiple_options ||
              prev[fieldId]?.multiple_options ||
              false,
            radioGroup: field.radio_group || prev[fieldId]?.radioGroup || "",
          },
        }));
        setMultipleOptions(field.multiple_options || false);
        // Initialize dropdown options from field
        if (field.options && Array.isArray(field.options)) {
          setDropdownOptions(field.options.length > 0 ? field.options : [""]);
        }
      }
    }
  }, [field, menuArray]);

  const config = getFieldConfig(field);

  const showCharacterLimit = config?.characterLimit;
  const fontSettings = config?.fontSettings;
  const dropdown = config?.inputType === "select";
  const required = config?.required;
  const initials = config?.name === "Initials";
  const radio = config?.inputType === "radio" || config?.name === "Radio";

  const handleDone = () => {
    const fieldId = field?.id || null;
    const fieldSettings = settings[fieldId] || {};

    // Update the individual field with new settings
    if (typeof setModalSettingsObj === "function") {
      setModalSettingsObj((prevSettings) => {
        const updated = {
          ...prevSettings,
          [fieldId]: {
            ...(prevSettings?.[fieldId] || {}),
            fontSize: fieldSettings.fontSize,
            fontStyle: fieldSettings.fontStyle,
            characterLimit: showCharacterLimit
              ? fieldSettings.characterLimit
              : null,
            required: fieldSettings.required,
            fieldType: field?.type,
            initials: fieldSettings.initials,
            multiple_options: fieldSettings.multiple_options,
          },
        };
        return updated;
      });
    }

    // Also update the field directly if updateField function is provided
    if (typeof updateField === "function") {
      const updatedField = {
        ...field,
        fontSize: fieldSettings.fontSize || field.fontSize,
        fontStyle: fieldSettings.fontStyle || field.fontStyle,
        characterLimit: showCharacterLimit
          ? fieldSettings.characterLimit
          : field.characterLimit,
        needed: fieldSettings.required,
        multiple_options: fieldSettings.multiple_options,
        options: dropdownOptions.filter((option) => option.trim() !== ""),
        radio_group: fieldSettings.radioGroup || field.radio_group, // Use settings radioGroup or fallback to field radio_group
      };
      updateField(updatedField);
    }

    if (typeof onHide === "function") {
      onHide();
    }
  };

  const updateCurrentSettings = (updates) => {
    const fieldId = field?.id || null;
    setSettings((prev) => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        ...updates,
      },
    }));
  };

  const handleAddOption = () => {
    setDropdownOptions((prev) => [...prev, ""]);
  };

  const handleRemoveOption = (index) => {
    setDropdownOptions((prev) => {
      const newOptions = prev.filter((_, i) => i !== index);
      return newOptions.length > 0 ? newOptions : [""];
    });
  };

  const handleOptionChange = (index, value) => {
    setDropdownOptions((prev) => {
      const newOptions = [...prev];
      newOptions[index] = value;
      return newOptions;
    });
  };

  return (
    <Modal show={show} centered onHide={onHide} className="common-popup">
      <Modal.Header className="settings-modal-header">
        <div className="form-modal">
          <Modal.Title>{`${field?.type} Settings`}</Modal.Title>
        </div>
        <CloseButton onClick={onHide} className="close-cta-wrapper" />
      </Modal.Header>
      <Modal.Body>
        {field && (
          <div className="field-settings-form">
            {initials && (
              <>
                <div className="form-group">
                  <label htmlFor="initials">Initials</label>
                  <select
                    id="initials"
                    className="form-control"
                    name="initials"
                    value={settings[field?.id]?.initials || ""}
                    onChange={(e) => {
                      updateCurrentSettings({ initials: e.target.value });
                    }}
                    style={{
                      MozAppearance: "number-input",
                      WebkitAppearance: "auto",
                      appearance: "auto",
                    }}
                  >
                    <option value="Select Initials">Select Initials</option>
                    <option value="Mine">Mine</option>
                    <option value="Employee">Employee</option>
                  </select>
                </div>
              </>
            )}
            {fontSettings && (
              <>
                <div className="form-group">
                  <label htmlFor="fontSize">Font Size</label>
                  <input
                    type="number"
                    id="fontSize"
                    className="form-control"
                    placeholder="Enter font size"
                    value={settings[field?.id]?.fontSize || ""}
                    name="fontSize"
                    min="0"
                    style={{
                      MozAppearance: "number-input",
                      WebkitAppearance: "auto",
                      appearance: "auto",
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      const clampedValue = value < 0 ? "0" : value;
                      updateCurrentSettings({ fontSize: clampedValue });
                    }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="fontStyle">Font Style</label>
                  <select
                    id="fontStyle"
                    className="form-control"
                    name="fontStyle"
                    value={settings[field?.id]?.fontStyle || ""}
                    onChange={(e) => {
                      updateCurrentSettings({ fontStyle: e.target.value });
                    }}
                    style={{
                      MozAppearance: "number-input",
                      WebkitAppearance: "auto",
                      appearance: "auto",
                    }}
                  >
                    <option value="">Select Font Style</option>
                    <option value="Lato">Lato</option>
                    <option value="Arial">Arial</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Helvetica">Helvetica</option>
                    <option value="Georgia">Georgia</option>
                  </select>
                </div>
              </>
            )}
            {showCharacterLimit && (
              <div className="form-group">
                <label htmlFor="characterLimit">
                  Character Limit
                  <img
                    src={InfoButton}
                    alt="info-button"
                    className="info-icon"
                    onClick={() => setShowTooltip(!showTooltip)}
                  />
                  {showTooltip && (
                    <div className="tooltip">
                      The maximum number of characters allowed in this field
                    </div>
                  )}
                </label>
                <input
                  type="number"
                  id="characterLimit"
                  className="form-control"
                  placeholder="Enter character limit"
                  value={settings[field?.id]?.characterLimit || ""}
                  name="characterLimit"
                  min="0"
                  style={{
                    MozAppearance: "number-input",
                    WebkitAppearance: "auto",
                    appearance: "auto",
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    const clampedValue = value < 0 ? "0" : value;
                    updateCurrentSettings({ characterLimit: clampedValue });
                  }}
                />
              </div>
            )}
            {required && (
              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="isRequired"
                  name="isRequired"
                  checked={settings[field?.id]?.required || false}
                  onChange={(e) => {
                    updateCurrentSettings({ required: e.target.checked });
                  }}
                />
                <label className="checkbox-label" htmlFor="isRequired">
                  Field is required
                </label>
              </div>
            )}
            {radio && (
              <div className="form-group">
                <label htmlFor="radioGroup">
                  Radio Group
                  <img
                    src={InfoButton}
                    alt="info-button"
                    className="info-icon"
                    onClick={() => setShowTooltip(!showTooltip)}
                  />
                  {showTooltip && (
                    <div className="tooltip radio-tooltip">
                      Group radio buttons together. Radio buttons with the same group name will be linked.
                    </div>
                  )}
                </label>
                <input
                  type="text"
                  id="radioGroup"
                  className="form-control"
                  placeholder="Enter group name (e.g., radio field name)"
                  value={settings[field?.id]?.radioGroup || field?.radio_group || ""}
                  name="radioGroup"
                  onChange={(e) => {
                    updateCurrentSettings({ radioGroup: e.target.value });
                    // Also update the field directly for immediate feedback
                    if (typeof updateField === "function") {
                      updateField({ ...field, radio_group: e.target.value });
                    }
                  }}
                />
              </div>
            )}
            {dropdown && (
              <>
                <h3 className="dropdown-settings-heading">Dropdown Settings</h3>
                {dropdownOptions.map((option, index) => (
                  <div key={index} className="form-group dropdown-option-group">
                    <label htmlFor={`option-${index}`}>
                      Option {index + 1}
                    </label>
                    <div className="option-input-wrapper">
                      <input
                        type="text"
                        id={`option-${index}`}
                        className="form-control"
                        value={option}
                        placeholder={`Option ${index + 1}`}
                        onChange={(e) =>
                          handleOptionChange(index, e.target.value)
                        }
                      />
                      {dropdownOptions.length > 1 && (
                        <img
                          src={DropdownDelete}
                          alt="delete option"
                          className="delete-option-icon"
                          onClick={() => handleRemoveOption(index)}
                        />
                      )}
                    </div>
                  </div>
                ))}
                <CommonButton
                  variant="secondary"
                  btnText="Add Option"
                  onClick={handleAddOption}
                />
                <div className="form-group checkbox-group multiple-options">
                  <input
                    type="checkbox"
                    id="allowMultipleSections"
                    name="allowMultipleSections"
                    checked={multipleOptions}
                    disabled={dropdownOptions.length <= 1}
                    onChange={(e) => {
                      setMultipleOptions(e.target.checked);
                      updateCurrentSettings({
                        multiple_options: e.target.checked,
                      });
                    }}
                  />
                  <label
                    className={`checkbox-label ${
                      dropdownOptions.length <= 1 ? "disabled" : ""
                    }`}
                    htmlFor="allowMultipleSections"
                  >
                    Allow multiple selections
                  </label>
                </div>
              </>
            )}
            <div className="update-button-wrapper">
              <CommonButton
                onClick={handleDone}
                variant="primary"
                btnText="DONE"
              />
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SettingsTaskModal;
